import { useCallback, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

interface Profile {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  role: string | null;
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  const loadProfile = useCallback(async (userId: string) => {
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();
      if (!error && data) {
        setProfile(data as Profile);
      }
    } catch {
      // ignore profile load errors, keep auth state intact
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          setSession(null);
          setUser(null);
          setLoading(false);
          setProfileLoading(false);
          return;
        }
        const currentSession = data?.session ?? null;
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setLoading(false);
        if (currentSession?.user) {
          void loadProfile(currentSession.user.id);
        } else {
          setProfileLoading(false);
        }
      })
      .catch(() => {
        if (!active) return;
        setSession(null);
        setUser(null);
        setLoading(false);
        setProfileLoading(false);
      });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!active) return;
        setSession(newSession);
        setUser(newSession?.user ?? null);
        if (newSession?.user) {
          void loadProfile(newSession.user.id);
        } else {
          setProfile(null);
          setProfileLoading(false);
        }
      }
    );

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const refreshProfile = useCallback(() => {
    if (user) {
      void loadProfile(user.id);
    }
  }, [user, loadProfile]);

  const isAdmin = profile?.role === "admin";

  return { session, user, profile, loading, profileLoading, isAdmin, refreshProfile };
}

export type { Profile };